import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';

const REASONS = [
  { value: 'correction', label: 'Correcting a mistake' },
  { value: 'name_change', label: 'Name change (e.g. marriage)' },
  { value: 'other', label: 'Other' },
];

const NI_REGEX = /^[A-Z]{2}\d{6}[A-D]$/;

function toISODate(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

export default function PersonalDetailsModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const original = {
    first_name: employee.first_name || '',
    last_name: employee.last_name || '',
    date_of_birth: toISODate(employee.date_of_birth),
    gender: employee.gender || '',
  };
  const [form, setForm] = useState({ ...original, ni_number: '', reason: 'correction', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const niClean = form.ni_number.replace(/\s/g, '').toUpperCase();
  const changed = {};
  for (const field of ['first_name', 'last_name', 'date_of_birth', 'gender']) {
    if (form[field].trim() !== original[field]) changed[field] = form[field].trim();
  }
  if (niClean) changed.ni_number = niClean;
  const hasChanged = Object.keys(changed).length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ('first_name' in changed && !changed.first_name) { setError('First name is required'); return; }
    if ('last_name' in changed && !changed.last_name) { setError('Last name is required'); return; }
    if ('date_of_birth' in changed && !changed.date_of_birth) { setError('Date of birth is required'); return; }
    if (changed.ni_number && !NI_REGEX.test(changed.ni_number)) {
      setError('Please enter a valid NI number (e.g. AB123456C)');
      return;
    }
    const payload = { reason: REASONS.find(r => r.value === form.reason)?.label || form.reason };
    for (const [field, value] of Object.entries(changed)) payload[`new_${field}`] = value;
    if (form.notes.trim()) payload.notes = form.notes.trim();

    setSaving(true);
    try {
      await onSave(employee.id, payload);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white';
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Personal Details - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="pd-first-name">First name</label>
                <input id="pd-first-name" type="text" name="first_name" value={form.first_name} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pd-last-name">Last name</label>
                <input id="pd-last-name" type="text" name="last_name" value={form.last_name} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="pd-dob">Date of birth</label>
                <DateInput
                  id="pd-dob"
                  name="date_of_birth"
                  value={form.date_of_birth}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="pd-gender">Gender</label>
                <select id="pd-gender" name="gender" value={form.gender} onChange={handleChange} className={inputCls}>
                  {!original.gender && <option value="">-- Select --</option>}
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="pd-ni">NI number</label>
              <input id="pd-ni" type="text" name="ni_number" value={form.ni_number} onChange={handleChange} className={inputCls}
                placeholder={employee.ni_number ? `Current ****${String(employee.ni_number).slice(-3)} — leave blank to keep` : 'AB123456C'} />
            </div>

            <div>
              <label className={labelCls} htmlFor="pd-reason">Reason</label>
              <select id="pd-reason" name="reason" value={form.reason} onChange={handleChange} className={inputCls}>
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className={labelCls} htmlFor="pd-notes">Notes (optional)</label>
              <textarea id="pd-notes" name="notes" rows="2" value={form.notes} onChange={handleChange} className={inputCls} />
            </div>

            {hasChanged && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                The employee record will be updated now and the change flagged for payroll processing.
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving || !hasChanged}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Update Details'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
